// Parses a free-form math string (pipe-separated equations, coordinate
// lists, or plain text) and renders it as a "whiteboard" of stacked rows
// with a sky-blue total banner snapped to the bottom.

interface Props {
  math: string;
  stepTitle?: string;
}

interface Row {
  label?: string;
  expression: string;
  result: string;
}

type Parsed =
  | { kind: 'coords'; items: string[]; banner: { label: string; value: string } | null }
  | { kind: 'equations'; rows: Row[]; banner: { label: string; value: string } | null }
  | { kind: 'plain'; text: string };

const COORD_RE =
  /\(\s*\w+\s*=\s*-?\d+(?:\.\d+)?(?:\s*,\s*\w+\s*=\s*-?\d+(?:\.\d+)?)*\s*\)/g;

// Guards against false positives like "d=3" (variable assignment inside prose).
// A real equation row has an arithmetic operator on the LHS and a clean number
// on the RHS.
function looksLikeEquation(row: string): boolean {
  const eqIdx = row.lastIndexOf('=');
  if (eqIdx < 0) return false;
  const lhs = row.slice(0, eqIdx).trim();
  const rhs = row.slice(eqIdx + 1).trim();

  // RHS must be a plain number, optionally followed by short units
  // ("22", "3.14", "22 sq units", "4 cubes").
  if (!/^-?\d+(?:\.\d+)?(?:\s+[A-Za-z][\w ]{0,20})?$/.test(rhs)) return false;

  // LHS must contain an arithmetic operator — otherwise it's a label/assignment
  // not a computation worth banner-styling.
  if (!/[+\-−×÷*/]/.test(lhs)) return false;

  // LHS cannot end with a single variable letter ("d=1", "x=2")
  if (/\b[a-zA-Z]$/.test(lhs)) return false;

  return true;
}

function parseMath(math: string, stepTitle?: string): Parsed {
  const trimmed = (math ?? '').trim();
  if (!trimmed) return { kind: 'plain', text: '' };

  // Coordinate list mode — "(y=1,z=1), (y=1,z=2) → 4 cubes"
  const coords = [...trimmed.matchAll(COORD_RE)];
  if (coords.length >= 2) {
    const last = coords[coords.length - 1];
    const afterCoords = trimmed.slice((last.index ?? 0) + last[0].length);
    const totalMatch = afterCoords.match(/[→=]\s*(.+?)$/);
    return {
      kind: 'coords',
      items: coords.map((c) => c[0]),
      banner: totalMatch
        ? { label: 'Cubes used', value: totalMatch[1].trim() }
        : null,
    };
  }

  // Prose red flags — em-dashes, "column of", "rectangle", etc. in an un-math-y
  // string means Claude put descriptive text in the math field. Render as prose.
  const looksProseHeavy =
    /—/.test(trimmed) || /\b(rectangle|staircase|column of|row of|view|shape|figure)\b/i.test(trimmed);

  // Equation list mode — split on |, newlines, or middle-dot
  const rawRows = trimmed
    .split(/\s*[|\n;·]\s*/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (rawRows.length === 0) return { kind: 'plain', text: trimmed };

  // If the string looks prose-heavy AND no row is a real equation, render as prose.
  const equationRowCount = rawRows.filter(looksLikeEquation).length;
  if (looksProseHeavy && equationRowCount === 0) {
    return { kind: 'plain', text: trimmed };
  }
  // If not a single row looks like an equation, don't pretend — render as prose.
  if (equationRowCount === 0) {
    return { kind: 'plain', text: trimmed };
  }

  const rows: Row[] = rawRows.map((row) => {
    const colonIdx = row.indexOf(':');
    const label = colonIdx > -1 ? row.slice(0, colonIdx).trim() : undefined;
    const rest = colonIdx > -1 ? row.slice(colonIdx + 1).trim() : row.trim();
    const eqIdx = rest.lastIndexOf('=');
    // Only treat as an equation row if the full row (with label) looks like one.
    if (eqIdx > -1 && looksLikeEquation(row)) {
      return {
        label,
        expression: rest.slice(0, eqIdx).trim(),
        result: rest.slice(eqIdx + 1).trim(),
      };
    }
    return { label, expression: rest, result: '' };
  });

  // Decide banner row: explicit Total/Answer/Result/Sum, else last row if 2+ rows
  // AND that last row is an equation.
  let bannerRow: Row | null = null;
  let bodyRows = rows;

  const totalIdx = rows.findIndex(
    (r) => r.label && /^(total|answer|result|sum)\b/i.test(r.label)
  );
  if (totalIdx !== -1 && rows[totalIdx].result) {
    bannerRow = rows[totalIdx];
    bodyRows = rows.filter((_, i) => i !== totalIdx);
  } else if (rows.length >= 2 && rows[rows.length - 1].result) {
    bannerRow = rows[rows.length - 1];
    bodyRows = rows.slice(0, -1);
  }

  const banner = bannerRow
    ? { label: deriveBannerLabel(bannerRow.label, stepTitle), value: bannerRow.result }
    : null;

  return { kind: 'equations', rows: bodyRows, banner };
}

function deriveBannerLabel(rowLabel: string | undefined, stepTitle?: string): string {
  if (rowLabel && /^(answer|result)\b/i.test(rowLabel)) return rowLabel;
  if (stepTitle) {
    // "Part (a): Greatest surface area — fill the bounding box" → "Greatest surface area"
    const partMatch = stepTitle.match(/^Part\s*\([a-z]\)\s*:?\s*(.+?)(?:\s*[—-]\s|$)/i);
    if (partMatch) return partMatch[1].trim();
    // "Subtract the shared faces" etc. — use first clause up to ~30 chars
    const firstClause = stepTitle.split(/\s*[—–-]\s/)[0].trim();
    if (firstClause.length <= 40) return firstClause;
  }
  if (rowLabel && /^(total|sum)\b/i.test(rowLabel)) return 'Total';
  return 'Answer';
}

export default function MathBlock({ math, stepTitle }: Props) {
  const parsed = parseMath(math, stepTitle);

  if (parsed.kind === 'plain') {
    if (!parsed.text) return null;
    // Prose-style block — sans-serif, relaxed line-height. Used when the `math`
    // field holds description rather than real equations.
    return (
      <div className="bg-cream border-l-4 border-sun/60 rounded-r-xl rounded-l-md px-4 py-3 text-sm leading-relaxed text-ink/85">
        {parsed.text}
      </div>
    );
  }

  const hasBanner = !!parsed.banner;
  const boardRounding = hasBanner ? 'rounded-t-xl border-b-0' : 'rounded-xl';

  return (
    <div>
      {parsed.kind === 'coords' && (
        <div
          className={`bg-cream border-2 border-sun/50 ${boardRounding} px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1.5`}
        >
          {parsed.items.map((coord, i) => (
            <div key={i} className="flex items-baseline gap-2 text-sm font-mono font-semibold">
              <span className="w-5 h-5 rounded-full bg-violet/25 text-violet text-[10px] font-sans font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <span>{coord}</span>
            </div>
          ))}
        </div>
      )}

      {parsed.kind === 'equations' && parsed.rows.length > 0 && (
        <div
          className={`bg-cream border-2 border-sun/50 ${boardRounding} px-4 py-3 font-mono text-sm leading-[1.9] space-y-0.5`}
        >
          {parsed.rows.map((row, i) => (
            <div key={i} className="flex justify-between items-baseline gap-3 pl-2">
              <span className="font-semibold text-ink">
                {row.label ? `${row.label}:  ` : ''}
                {row.expression}
              </span>
              {row.result && (
                <span className="text-sky font-extrabold whitespace-nowrap">= {row.result}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty body + banner case — banner is fully rounded */}
      {hasBanner && parsed.kind === 'equations' && parsed.rows.length === 0 && null}

      {parsed.banner && (
        <div
          className={`bg-sky text-white ${
            (parsed.kind === 'equations' && parsed.rows.length === 0) ||
            (parsed.kind === 'coords' && parsed.items.length === 0)
              ? 'rounded-xl'
              : 'rounded-b-xl'
          } px-4 py-2.5 flex justify-between items-baseline font-display font-black`}
        >
          <span className="text-sm flex items-center gap-1.5">
            <span>✓</span>
            <span>{parsed.banner.label}</span>
          </span>
          <span className="font-mono text-base">{parsed.banner.value}</span>
        </div>
      )}
    </div>
  );
}
